import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import DxTable, { DxLookup, DxTableColumn } from '../../components/common/DxTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { ProductCategoryModel } from '../../context/models/products-category/ProductCategoryModel';
import { ProductCategoryActionTypes } from '../../context/stores/products-categroy/IProductCategoryActionsTypes';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import { createUUID } from '../../utils/createUUID';
import ProductCategoryDetail from './ProductCategoryDetail';

type IProductCategoryPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allProductCategorys: ProductCategoryModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<ProductCategoryModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: ProductCategoryModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<ProductCategoryModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Danh mục sản phẩm`,
		status: state.productCategory.status,
		allProductCategorys: state.productCategory.allProductCategorys,
		selectedIds: state.productCategory.selectedIds,
		showDetail: state.productCategory.showDetail,
		showConfirm: state.productCategory.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ProductCategoryActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.productCategory.getAllRequest());
	},
	handleSaveData: (data: ProductCategoryModel) => {
		dispatch(actions.productCategory.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.productCategory.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.productCategory.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<ProductCategoryModel>) => {
		dispatch(actions.productCategory.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.productCategory.showConfirm(isShow));
	},
});

const ProductCategoryPage = (props: IProductCategoryPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allProductCategorys,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): any => {
		let result: any = [
			<DxTableColumn key={createUUID()} dataField='name' caption={`Tên`} width={150}></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='slug' minWidth={150} caption={`Slug`}></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='parentId' width={150} caption={`Danh mục cha`}>
				<DxLookup dataSource={allProductCategorys} valueExpr={'id'} displayExpr={'name'} />
			</DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='keyWords' width={150} caption={`Từ khóa`}></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='description' width={150} caption={`Mô tả`}></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='inActive' width={80} caption={`Trạng thái`} falseText='Áp dụng' trueText='Không'></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='isHidden' width={80} caption={`Ẩn Menu chính`} falseText='Hiển thị' trueText='Ẩn'></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='isHiddenSub' width={80} caption={`Ẩn Menu phụ`} falseText='Hiển thị' trueText='Ẩn'></DxTableColumn>,
			<DxTableColumn key={createUUID()} dataField='isHiddenFooter' width={80} caption={`Ẩn Menu chân trang`} falseText='Hiển thị' trueText='Ẩn'></DxTableColumn>,
		];
		result.push(
			<DxTableColumn
				visibleIndex={1}
				key={createUUID()}
				width={50}
				caption={'Sửa'}
				alignment='center'
				allowExporting={true}
				cellRender={(cell: any) => {
					return (
						<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: cell.data })} title='Sửa' icon='ni ni-edit' theme='link' />
					);
				}}
			/>
		);
		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='nk-fmg-actions'>
						<ul className='nk-block-tools g-3'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='d-md-inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='d-md-inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DxTable
								dataSource={allProductCategorys}
								keyExpr='id'
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								defaultPageSize={50}
								filters={{
									refreshDataGrid: handleReloadAllData,
								}}
								selection={{
									mode: 'multiple',
									onSelectionChanged: (e: any) => {
										onChangeSelectedIds(e.selectedRowKeys || []);
									},
									selectedRowKeys: selectedIds,
								}}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<ProductCategoryDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCategoryPage);
